import { format } from 'date-fns';
import { Suspense } from 'react';
import { BUTTON_VARIANTS } from '@workspace/constants/common.constant';
import { stripInvisibles } from '@workspace/constants';
import { cn, getBackgroundStyle, openDocsBot, shouldOpenInNewTab } from '@workspace/lib';
import { processHeading, type HeadingType } from '@workspace/lib/heading-utils';
import { Button, type ButtonVariant } from '@workspace/ui/common/Buttons/Button';
import { ShareIcons } from '@workspace/ui/common/Links/ShareIcons';
import { Typography } from '@workspace/ui/common/Typography/Typography';
import { HeroBannerSize } from '../hero-banner.types';
import { getHeroBannerMinHeightClass } from '../hero-banner-util';

interface HeroBannerContentProps {
  tag?: string;
  heading: {
    _type: string;
    fontSize?: 'h1' | 'h2' | 'h3';
    headingText: string;
  };
  description?: string;
  contentPosition: 'left' | 'center' | 'right';
  textColor?: string;
  buttons?: Array<{
    anchor?: string | null;
    isOpenInNewTab?: boolean | null;
    label: string | null;
    linkType?: 'external' | 'internal' | 'anchor' | 'askGreg';
    style?: string | null;
    url?: string | null;
  }> | null;
  socialLinks?: Array<{
    platform: string;
    color?: 'light' | 'dark';
    url?: string;
    isOpenInNewTab?: boolean;
    iconUrl?: string;
    link?: {
      url?: string;
      isOpenInNewTab?: boolean;
    };
    _ref?: string;
    _type?: 'reference';
  } | null>;
  shareIcons?: Array<{
    shareType: string;
    sharePath?: string;
    iconUrl?: string;
  } | null>;
  authorBlog?: string;
  publicDataBlog?: string;
  size?: HeroBannerSize;
}

const textAlignmentClassMap: Record<'left' | 'center' | 'right', string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const buttonVariantValues: readonly ButtonVariant[] = BUTTON_VARIANTS as readonly ButtonVariant[];

const normalizeButtonVariant = (style?: string | null): ButtonVariant => {
  if (!style) return 'primary';

  const sanitized = style
    .replace(/\p{C}/gu, '')
    .replace(/button$/iu, '')
    .trim()
    .toLowerCase();

  if (buttonVariantValues.includes(sanitized as ButtonVariant)) {
    return sanitized as ButtonVariant;
  }

  return 'primary';
};

export function HeroBannerContent({
  tag,
  heading,
  description,
  contentPosition,
  textColor,
  buttons,
  socialLinks,
  shareIcons,
  authorBlog,
  publicDataBlog,
  size = 'medium',
}: HeroBannerContentProps) {
  const { text: rawHeadingText } = processHeading(heading as HeadingType);
  // Normalize non-breaking spaces to regular spaces for proper text wrapping
  const resolvedHeadingText = rawHeadingText?.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');
  const headingClass = 'text-9 leading-15 md:text-xl md:leading-14 font-bold';
  const minHeightClass = getHeroBannerMinHeightClass(size);

  // Get gap classes based on size and screen size
  const getGapClasses = (size: HeroBannerSize): string => {
    switch (size) {
      case 'full':
        return 'gap-sm md:gap-lg'; // Mobile: 16px, Desktop: 32px
      case 'medium':
        return 'gap-sm md:gap-md'; // Mobile: 16px, Desktop: 24px
      case 'slim':
        return 'gap-sm'; // Mobile: 16px, Desktop: 16px
      default:
        return 'gap-sm md:gap-md'; // Default to medium
    }
  };

  // Safety check: ensure we always have some content to display
  const hasContent =
    resolvedHeadingText ||
    tag ||
    description ||
    (buttons && buttons.length > 0) ||
    (socialLinks && socialLinks.length > 0);

  // Use getBackgroundStyle to get color value, then extract the backgroundColor
  const colorValue = getBackgroundStyle(textColor).backgroundColor;

  // Handle author and publication date formatting
  const publicDataBlogRender = publicDataBlog ? format(new Date(publicDataBlog), 'd MMMM yyyy') : null;

  const metaArticle =
    authorBlog && publicDataBlog
      ? `By ${authorBlog} ${publicDataBlogRender}`
      : authorBlog
        ? `By ${authorBlog}`
        : publicDataBlog
          ? `Published on ${publicDataBlogRender}`
          : '';

  const handleAskGregClick = () => {
    openDocsBot();
  };

  return (
    <div
      className={cn(
        'hero-container transition-all duration-700 flex flex-col justify-center h-auto',
        getGapClasses(size),
        minHeightClass,
        contentPosition === 'left' ? 'max-w-175 items-start' : '',
        textAlignmentClassMap[contentPosition],
        contentPosition === 'center' && 'mx-auto items-center',
        contentPosition === 'right' && 'ml-auto items-end',
        'max-md:mx-auto',
      )}
    >
      {tag && (
        <div className="bg-secondary-50 bg-opacity-70 rounded-md px-sm py-xs inline-block w-max">
          <Typography variant="button" className="whitespace-pre-line text-black leading-5 h-5 block font-sora">
            {tag}
          </Typography>
        </div>
      )}

      {(resolvedHeadingText || !hasContent) && (
        <Typography
          variant="h1"
          className={cn(headingClass, 'break-words !font-sora text-4xl md:text-6xl')}
          style={{ color: colorValue }}
        >
          {resolvedHeadingText}
        </Typography>
      )}

      {description && (
        <div
          className={cn(
            contentPosition === 'left' && '!max-w-146.5',
            contentPosition === 'center' && '!max-w-146.5 mx-auto',
            contentPosition === 'right' && '!max-w-146.5 ml-auto',
          )}
        >
          <Typography
            variant="bodySm"
            className="!font-openSans whitespace-pre-line !text-sm md:!text-base  !font-semibold"
            style={{ color: colorValue }}
          >
            {description}
          </Typography>
        </div>
      )}

      {metaArticle && (
        <div
          className={cn(
            contentPosition === 'center' && '!max-w-146.5 mx-auto',
            contentPosition === 'right' && '!max-w-146.5 ml-auto',
            contentPosition === 'left' && '!max-w-146.5',
          )}
        >
          <Typography
            variant="bodySm"
            className="!font-openSans whitespace-pre-line !text-sm md:!text-base "
            style={{ color: colorValue }}
          >
            {metaArticle}
          </Typography>
        </div>
      )}

      {Array.isArray(buttons) && buttons.length > 0 && (
        <div
          className={cn(
            'flex gap-xs md:gap-sm w-full max-sm:flex-col mt-xs md:mt-0 z-50',
            contentPosition === 'center' && 'justify-center',
            contentPosition === 'right' && 'justify-end',
          )}
        >
          {buttons.map((btn, index) => {
            const label = stripInvisibles(btn.label || '').trim();
            return (
              <Button
                key={index}
                variant={normalizeButtonVariant(btn.style)}
                url={
                  btn.linkType === 'askGreg'
                    ? undefined
                    : btn.linkType === 'anchor'
                      ? btn.anchor || undefined
                      : btn?.url || undefined
                }
                openInNewTab={shouldOpenInNewTab({
                  type: btn.linkType,
                  isOpenInNewTab: btn.isOpenInNewTab,
                })}
                onClick={btn.linkType === 'askGreg' ? handleAskGregClick : undefined}
                className="max-h-11 w-full md:max-w-max !font-semibold md:min-w-5xl gap-sm min-w-0 z-50"
              >
                <span className="truncate md:min-w-max">{btn.linkType === 'askGreg' ? 'Ask Greg' : label}</span>
              </Button>
            );
          })}
        </div>
      )}

      {Array.isArray(shareIcons) && shareIcons.length > 0 && (
        <div
          className={cn(
            'flex gap-5',
            contentPosition === 'center' && 'justify-center',
            contentPosition === 'right' && 'justify-end',
          )}
        >
          <Suspense fallback={null}>
            <ShareIcons
              icons={shareIcons.filter((icon): icon is NonNullable<typeof icon> => icon !== null && !!icon?.shareType)}
              className="text-white"
              // socialLinksData={socialLinks}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
